import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import { Lightbulb, AddCircleOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export const UnableToFetchInsightsHero: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ mt: 4, mb: 3 }}>
      <Paper
        elevation={4}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${
            theme.palette.primary.main
          } 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%)`,
          position: "relative",
        }}
      >
        <Grid container>
          {/* Left Side - Content */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              p: { xs: 4, md: 6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Lightbulb
                sx={{
                  fontSize: 32,
                  color: "#FFD700",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  fontWeight: 600,
                }}
              >
                Get Started
              </Typography>
            </Box>

            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                color: "white",
                mb: 2,
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                lineHeight: 1.2,
              }}
            >
              Unable to fetch upselling insights
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                mb: 4,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.6,
              }}
            >
              To unlock powerful upselling insights and boost your revenue, you
              need to have active service items and venues configured in your
              system.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<AddCircleOutline />}
                onClick={() => navigate("/packages/manage")}
                sx={{
                  bgcolor: "white",
                  color: "primary.main",
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.95)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                Create Service Item
              </Button>
            </Box>

            {/* Quick Info Cards */}
            <Box
              sx={{
                mt: 4,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  flex: 1,
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    mb: 0.5,
                  }}
                >
                  What you'll get
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "white", fontWeight: 600 }}
                >
                  📊 Real-time analytics
                </Typography>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  flex: 1,
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    mb: 0.5,
                  }}
                >
                  Auto-generate
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "white", fontWeight: 600 }}
                >
                  🚀 Smart campaigns
                </Typography>
              </Paper>
            </Box>
          </Grid>

          {/* Right Side - Illustration */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              position: "relative",
            }}
          >
            <Box
              component="img"
              src={require("../../../../assets/images/backgrounds/upsell-bg-img.jpg")}
              alt="Upselling Success"
              sx={{
                borderRadius: 2,
                width: "100%",
                maxWidth: 400,
                height: "auto",
                filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.2))",
                animation: "float 3s ease-in-out infinite",
                "@keyframes float": {
                  "0%, 100%": { transform: "translateY(15px)" },
                  "50%": { transform: "translateY(-15px)" },
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
